import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  ArrowLeft,
  Users,
  UserCheck,
  UserPlus,
  Search,
  Clock,
  X,
  ChevronRight
} from 'lucide-react';

function Connections() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { 
    users, 
    friends, 
    followers, 
    following, 
    followRequests,
    followUser,
    getFollowStatus,
    loading 
  } = useApp();
  
  // Get initial tab from URL params
  const initialTab = searchParams.get('tab') === 'followers' ? 'followers' : 'following';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Update tab when URL changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'followers' || tabParam === 'following') {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) return [];
    const query = searchQuery.toLowerCase();
    return users
      .filter(u => 
        u.id !== user?.id && 
        (u.username?.toLowerCase().includes(query) || 
         u.displayName?.toLowerCase().includes(query))
      )
      .slice(0, 5);
  }, [searchQuery, users, user]);

  const handleFollow = async (userId) => {
    try {
      await followUser(userId);
      setSearchQuery('');
      setShowSearch(false);
    } catch (error) {
      console.error('Error following user:', error);
      alert(error.message || 'Failed to follow user');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    navigate('/auth');
    return null;
  }

  // Get user objects for following/followers
  const followingUsers = users.filter(u => (following || friends).includes(u.id));
  const followerUsers = users.filter(u => followers?.includes(u.id));

  const renderUserList = (userList, emptyMessage) => {
    if (userList.length === 0) {
      return (
        <div className="text-center py-12 bg-dive-dark border border-dive-gray rounded-xl">
          <Users className="w-12 h-12 text-dive-gray mx-auto mb-3" />
          <h3 className="text-white text-lg mb-2">{emptyMessage}</h3>
          <button
            onClick={() => setShowSearch(true)}
            className="text-dive-neon text-sm hover:underline"
          >
            Find people to follow
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {userList.map(person => (
          <Link
            key={person.id}
            to={`/social/${person.id}`}
            className="block bg-dive-dark border border-dive-gray rounded-xl p-4 hover:border-dive-neon/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-dive-neon/20 flex items-center justify-center overflow-hidden">
                {person.avatarUrl ? (
                  <img src={person.avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-dive-neon font-medium text-lg">
                    {person.displayName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{person.displayName}</p>
                <p className="text-gray-500 text-sm">@{person.username}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500" />
            </div>
          </Link>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-dive-black py-6 pb-24">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/profile')}
            className="p-2 text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-display text-xl text-white tracking-wide">CONNECTIONS</h1>
          <button
            onClick={() => setShowSearch(!showSearch)}
            className={`p-2 transition-colors ${showSearch ? 'text-dive-neon' : 'text-gray-400 hover:text-dive-neon'}`}
            title="Find people to follow"
          >
            <UserPlus className="w-5 h-5" />
          </button>
        </div>

        {/* Search Section */}
        {showSearch && (
          <div className="mb-6 bg-dive-dark border border-dive-gray rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-dive-neon" />
                <h3 className="text-white font-medium">Find People</h3>
              </div>
              <button
                onClick={() => {
                  setShowSearch(false);
                  setSearchQuery('');
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search by username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10"
                autoFocus
              />
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-3 space-y-2">
                {searchResults.map(searchUser => {
                  const isFollowing = (following || friends).includes(searchUser.id);
                  const isPending = followRequests?.outgoing?.some(r => r.following_id === searchUser.id);
                  return (
                    <div
                      key={searchUser.id}
                      className="flex items-center justify-between p-2 bg-dive-darker rounded-lg"
                    >
                      <Link 
                        to={`/social/${searchUser.id}`}
                        className="flex items-center gap-2 flex-1 hover:opacity-80 transition-opacity"
                        onClick={() => setShowSearch(false)}
                      >
                        <div className="w-8 h-8 rounded-full bg-dive-neon/20 flex items-center justify-center overflow-hidden">
                          {searchUser.avatarUrl ? (
                            <img src={searchUser.avatarUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-dive-neon text-sm">
                              {searchUser.displayName.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="text-white text-sm">{searchUser.displayName}</p>
                          <p className="text-gray-500 text-xs">@{searchUser.username}</p>
                        </div>
                      </Link>
                      {!isFollowing && !isPending && (
                        <button
                          onClick={() => handleFollow(searchUser.id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-dive-neon text-white text-sm rounded-lg hover:bg-dive-neon/80"
                        >
                          <UserPlus className="w-4 h-4" />
                          Follow
                        </button>
                      )}
                      {isPending && (
                        <span className="text-gray-500 text-sm flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Requested
                        </span>
                      )}
                      {isFollowing && (
                        <span className="text-dive-neon text-sm flex items-center gap-1">
                          <UserCheck className="w-4 h-4" />
                          Following
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            
            {searchQuery && searchQuery.length >= 2 && searchResults.length === 0 && (
              <p className="text-gray-500 text-sm mt-3 text-center">
                No users found matching "{searchQuery}"
              </p>
            )}
          </div>
        )}

        {/* Tabs - Styled like Bars page */}
        <div className="flex bg-dive-darker rounded-lg p-1 mb-6">
          <button
            onClick={() => setActiveTab('following')}
            className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'following'
                ? 'bg-dive-neon text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Users className="w-4 h-4" />
            Following ({followingUsers.length})
          </button>
          <button
            onClick={() => setActiveTab('followers')}
            className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'followers'
                ? 'bg-purple-500 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <UserCheck className="w-4 h-4" />
            Followers ({followerUsers.length})
          </button>
        </div>

        {/* Content */}
        {activeTab === 'following' && renderUserList(followingUsers, 'Not following anyone yet')}
        {activeTab === 'followers' && renderUserList(followerUsers, 'No followers yet')}
      </div>
    </div>
  );
}

export default Connections;
